import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from './utils';
import { Spinner } from 'components/app-components/ui/spinner';
import { Tooltip } from 'components/app-components/ui/tooltip';
import { formatButtonText } from 'utils/string';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&>svg]:pointer-events-none [&>svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-4 shrink-0 [&>svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-therablue focus-visible:ring-1 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        // Legacy thera-button "default" = transparent with hover opacity (~53 call sites)
        default: 'hover:opacity-70',
        // Primary blue button (shadcn default behavior)
        primary:
          'bg-therablue font-medium text-white transition hover:bg-therablue-light active:bg-therablue-dark focus:outline-none focus:ring-blue-500 focus:ring-offset-2 border border-therablue min-w-20',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border border-neutral-200 bg-white font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-blue-500 focus:ring-offset-2 min-w-20',
        secondary:
          'bg-transparent border border-neutral-200 font-medium text-neutral-900 shadow-sm transition hover:bg-neutral-100 active:bg-blue-100 min-w-20',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        plain: 'bg-transparent !p-0 h-auto hover:bg-transparent hover:opacity-70',
        link: 'text-primary underline-offset-4 hover:underline',

        // Thera-specific variants (from legacy thera-button)
        icon: 'ml-auto inline-flex items-center bg-transparent text-gray-400 hover:bg-gray-200 hover:text-gray-900',
        theraOutline:
          'rounded-md border border-neutral-200 text-sm text-neutral-900 transition hover:bg-neutral-100 md:w-auto',
        // Inverts to a light pill on dark surfaces. The dark values are literal hex, not
        // `bg-white`/`text-neutral-900`, because those tokens are themselves remapped and
        // resolve to dark values under `.dark`, so using them here paints a dark-on-dark button.
        // They are `!` so they win over the `!bg-neutral-800` several call sites pass in.
        theraPay:
          'rounded-md bg-neutral-900 py-2.5 font-semibold text-white transition hover:bg-darkColor-light active:bg-darkColor-active min-w-20 dark:!bg-[#ffffff] dark:!text-[#171717] dark:hover:!bg-[#e5e5e5] dark:active:!bg-[#d4d4d4]',
        theraRedOutline:
          'text-red-600 rounded-[6px] border border-[#CF2E25] bg-white py-[8px] text-base font-medium first-line:border-opacity-40 hover:cursor-pointer hover:bg-red-50 focus:outline-none focus:ring-red-500 focus:ring-offset-2 min-w-20',
      },
      // Every size states its own height. Without one the height falls out of padding plus the
      // content's line-height, so an icon or a spinner silently resized the button and two sizes
      // from different rows never lined up. Scale is 32 / 40 / 44 / 48, with icon square at 36.
      size: {
        default: 'h-10 px-4 py-2 has-[>svg]:px-3',
        theraDefault: 'h-11 px-4 py-3 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-11 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9 rounded-md',

        // Legacy thera-button sizes
        small: 'h-8 py-1.5 px-3.5 text-sm rounded-md !min-w-10',
        medium: 'h-10 py-2.5 px-3.5 text-sm rounded-md',
        large: 'h-12 py-3 px-5 text-lg rounded-lg',
        xsmall: 'h-8 rounded-lg p-1.5 text-sm',
      },
    },
    compoundVariants: [
      // Text-style buttons sit inline in a sentence or a toolbar, so they keep their content
      // height whatever size is passed. Declared here because cva emits this after size, and
      // twMerge takes the last of two conflicting heights.
      { variant: 'plain', class: 'h-auto' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  },
);

// Legacy class maps — used by a few files that style Links/anchors as buttons.
export const btnClassesByVariant = {
  primary:
    'bg-therablue font-medium text-white transition hover:bg-therablue-light active:bg-therablue-dark focus:outline-none focus:ring-blue-500 focus:ring-offset-2 border border-therablue min-w-20',
  secondary:
    'bg-transparent border border-neutral-200 font-medium text-neutral-900 transition hover:bg-neutral-100 active:bg-blue-100 min-w-20',
  outline:
    'border border-neutral-200 bg-white font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-blue-500 focus:ring-offset-2 min-w-20',
  icon: 'ml-auto inline-flex items-center bg-transparent text-gray-400 hover:bg-gray-200 hover:text-gray-900',
  plain: 'p-0 bg-transparent hover:opacity-70',
  default: 'hover:opacity-70',
  theraOutline:
    'rounded-md border border-neutral-200 text-sm text-neutral-900 transition hover:bg-neutral-100 md:w-auto',
  theraPay:
    'rounded-md bg-neutral-900 py-2.5 font-semibold text-white transition hover:bg-darkColor-light active:bg-darkColor-active min-w-20 dark:!bg-[#ffffff] dark:!text-[#171717] dark:hover:!bg-[#e5e5e5] dark:active:!bg-[#d4d4d4]',
  theraRedOutline:
    'text-red-600 rounded-[6px] border border-[#CF2E25] bg-white py-[8px] text-base font-medium first-line:border-opacity-40 hover:cursor-pointer hover:bg-red-50 focus:outline-none focus:ring-red-500 focus:ring-offset-2 min-w-20',
};

export const btnClassesBySize = {
  small: 'py-1.5 px-3.5 text-sm rounded-md !min-w-10',
  medium: 'py-2.5 px-3.5 text-sm rounded-md',
  large: 'py-3 px-5 text-lg rounded-lg',
  xsmall: 'rounded-lg p-1.5 text-sm',
};

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
    /** @deprecated Use `type` instead */
    htmlType?: 'submit' | 'button';
    /** @deprecated Use `form` instead */
    formId?: string;
    tooltipContent?: string | React.ReactNode;
    themeOfTooltip?: { target: string };
    transition?: boolean;
    isDarkMode?: boolean;
    loaderStrokeColor?: string;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      disabled,
      children,
      htmlType,
      formId,
      tooltipContent,
      themeOfTooltip = { target: 'w-fit' },
      transition = false,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isDarkMode,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      loaderStrokeColor,
      type: _type,
      form: _form,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const content = typeof children === 'string' ? formatButtonText(children) : children;

    const btn = (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(
          buttonVariants({ variant, size, className }),
          transition && 'transition duration-300 ease-in-out hover:-translate-y-0.5',
        )}
        type={htmlType || _type || 'button'}
        form={formId || _form}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        translate="no"
        {...props}
      >
        {!loading ? (
          content
        ) : asChild ? (
          // Slot clones exactly one child.
          <Spinner className="text-current" />
        ) : (
          <>
            <Spinner className="text-current" />
            {/* The spinner covers the label on screen; screen readers still need it. */}
            <span className="sr-only">{content}</span>
          </>
        )}
      </Comp>
    );

    if (tooltipContent) {
      return (
        <Tooltip
          theme={themeOfTooltip}
          content={tooltipContent}
          className="min-w-max max-w-min !text-xs font-medium leading-3 shadow-lg"
        >
          {btn}
        </Tooltip>
      );
    }

    return btn;
  },
);

Button.displayName = 'Button';

export default Button;
export { Button, buttonVariants };
